import { useEffect, useRef } from "react";
import { useAdk, useApiService } from "@art/shared-ui";
import { useAppDefaults } from "../../store";

const parse = (value: unknown): any => {
  if (typeof value !== "string") return value;
  try {
    return JSON.parse(value);
  } catch {
    return null;
  }
};

const ArtTrixyGuide = () => {
  const adk = useAdk() as any;
  const apiservice = useApiService();
  const { defaults: appDefaults } = useAppDefaults();


  const adkRef = useRef(adk);
  adkRef.current = adk;
  const coachMarkAgentRef = useRef("");
  coachMarkAgentRef.current = appDefaults?.coach_mark_agent_id?.trim() || "";

  const threadRef = useRef<{ adk: any; thread: any } | null>(null);


  const getThread = (coachMarkAgent: string) => {
    if (threadRef.current?.adk !== adkRef.current) {
      threadRef.current?.thread.dispose();
      threadRef.current = {
        adk: adkRef.current,
        thread: adkRef.current.agent(coachMarkAgent).thread(crypto.randomUUID()),
      };
    }
    return threadRef.current!.thread;
  };

  // 2. Send user message → resolves with the agent's reply
  const handleSendMessage = async (inputMessage: string): Promise<any> => {
    const coachMarkAgent = coachMarkAgentRef.current;
    if (!coachMarkAgent) {
      apiservice.showSnack("The guide isn't configured for this organisation yet.", "warning");
      return null;
    }
    if (!adkRef.current) {
      apiservice.showSnack("The guide is still connecting. Please try again in a moment.", "warning");
      return null;
    }

    try {
      const currentUrl = window.location.pathname;
      const run = await getThread(coachMarkAgent).run(`[current_url=${currentUrl}] ${inputMessage}`);
      return parse(await run.done());
    } catch (error: any) {
      // done() rejects with the agent's own error ({ code, message }).
      console.error("[TrixianGuide] agent failed:", error);
      apiservice.showSnack(error?.message || "Failed to send message", "error");
      return null;
    }
  };

  // 3. Wire mascot → agent
  useEffect(() => {
    (window as any).TrixianGuide?.init({
      greetings: [
        "Hey there! 👋",
        "Welcome!\nNeed any help?",
        "I'm right here for you!",
      ],
      mode: "companion",
      getActions: async (message: string) => {
        let response = await handleSendMessage(message);
        if (!response) return [];

   
        if (!response?.data?.output_data) response = parse(response?.message) ?? response;

        const output = response?.data?.output_data ?? response?.output_data;
        if (!Array.isArray(output) || output.length === 0) {
          return response?.message ? { question: response.message } : [];
        }

        const first = output[0];
        if (first?.action === "" && first?.question) {
          return { question: first.question };
        }

        return output;
      },
      getTranscription: async (blob: Blob) => {
        const form = new FormData();
        form.append("file", blob, "voice.webm");
        form.append("response_format", "json");

        form.append("temperature", "0");

        try {
          const response = await apiservice.post(
            "util-features/translations",
            form,
            { headers: { "Content-Type": undefined as any } },
          );
          const text = response?.data?.data?.text ?? response?.data?.text ?? "";
          return typeof text === "string" ? text : "";
        } catch (err) {
          console.error("[TrixianGuide] transcription failed:", err);
          apiservice.showSnack("Voice transcription failed", "error");
          throw err;
        }
      },
    });
  }, []);

  return null;
};

export default ArtTrixyGuide;
